import type { RecordsGatewayPort } from "@/application/records/ports/RecordsGatewayPort";
import { RetryService } from "@/application/common/services/RetryService";
import { MetricsService } from "@/application/common/services/MetricsService";
import { ErrorHandlerService } from "@/application/common/services/ErrorHandlerService";
import { EventBus } from "@/application/common/events/EventBus";
import {
  assertMutableRecordId,
  assertNoDemoReportMutations,
} from "./demoReportMutationGuard";

export interface ChangeRecordStatusInputDto {
  recordId: string;
  status: string;
  userId?: string;
}

/**
 * Use case: changes record status (admin only)
 * Clean Architecture - Application Layer
 */
export class ChangeRecordStatusUseCase {
  private static readonly ALLOWED_STATUSES = [
    "Uploaded",
    "InQueue",
    "InProgress",
    "Finished",
    "Error",
  ] as const;

  constructor(
    private readonly gateway: RecordsGatewayPort,
    private readonly retryService: RetryService,
    private readonly metricsService: MetricsService,
    private readonly errorHandler: ErrorHandlerService,
    private readonly eventBus: EventBus
  ) {}

  async execute(input: ChangeRecordStatusInputDto): Promise<void> {
    const { recordId, status, userId } = input;
    if (!recordId || !status) {
      return;
    }

    const isAllowed = ChangeRecordStatusUseCase.ALLOWED_STATUSES.some(
      (allowed) => allowed.toLowerCase() === status.toLowerCase()
    );
    if (!isAllowed) {
      throw new Error("Invalid status value");
    }
    assertMutableRecordId(recordId);

    const startTime = Date.now();
    const context = {
      operation: "changeRecordStatus",
      userId,
      recordId,
      status,
    };

    try {
      await this.retryService.executeWithRetry(async () => {
        const request = {
          change_names: [],
          for_archive: [],
          for_delete: [],
          delete_link: [],
          assign_link: [],
          change_status: [
            {
              record_id: recordId,
              status: status,
            },
          ],
        };
        assertNoDemoReportMutations(request);
        await this.gateway.updateRecords(request);
      }, "changeRecordStatus");

      const duration = Date.now() - startTime;
      this.metricsService.recordMetric({
        operation: "changeRecordStatus",
        duration,
        success: true,
        metadata: { recordId, status },
      });
    } catch (error) {
      const duration = Date.now() - startTime;
      const errorObj =
        error instanceof Error ? error : new Error(String(error));

      this.metricsService.recordMetric({
        operation: "changeRecordStatus",
        duration,
        success: false,
        error: errorObj.message,
        metadata: { recordId, status },
      });

      const userMessage = this.errorHandler.handleError(errorObj, context);
      throw new Error(userMessage);
    }
  }
}
